import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from '../../../store';
import {SipCall} from '../../../store/actions/ringgroups/payloads';
import {ReduxState} from '../../../store/types';
import {
    ExtensionPresenceWidgetData,
    RefreshWidgetQueueItem,
    WidgetExtensionPresenseData,
    WidgetExtensionPresenseFilter,
    WidgetPresantationType
} from '../../../store/types/Wallboard';
import {Colors} from '../../../styles/Colors';
import {Doughnut} from 'react-chartjs-2';
import {
    filterBySelectedExtensions,
    filterBySelectedRingGroups,
    getAvailableExtensionsFromRingGroups,
    getLoggedOutExtensionsFromRingGroups,
    getUnregisteredExtensions,
    useStyles
} from './ExtensionPresenseWidget.utils';
import AnimatedNumberText from '../../../components/AnimatedNumber/AnimatedNumber';
import EmptyGridPlaceholder from '../../../components/DataGrid/EmptyGridPlaceholder';
import WidgetLegendItem, {ChartLegendItem} from './WidgetLegendItem';
import {ExtensionType} from '../../../store/types/Extension';
import {RingGroupType} from '../../../store/types/RingGroup';
import WidgetLoadingMask from '../WidgetLoadingMask';
import {getStatus} from '../../../hooks/agents/useAgentsHook';
import {ExtensionStatusItem} from "./ExtensionStatusItem";
import WidgetHeaderWrapper from "../../../components/WidgetHeaderWrapper/WidgetHeaderWrapper";

export type ExtensionPresenceWidgetProps = {
    data: WidgetExtensionPresenseData,
    id: string,
    presentation: WidgetPresantationType,
    darkMode: boolean,
    autoRefreshTime?:number
};

type Timeout = ReturnType<typeof setTimeout>;

const ExtensionPresenceWidget: React.VFC<ExtensionPresenceWidgetProps> = (
    {
        data,
        id,
        presentation,
        darkMode,
        autoRefreshTime
    }) => {

    const classes = useStyles();
    const initRender = useRef(true)
    const [isChecked, setIsChecked] = useState<boolean>(false)
    const refreshWidgetsQueue: RefreshWidgetQueueItem[] = useSelector((state: ReduxState) => state.wallboard.refreshWidgetsQueue);

    const [extensionsList, setExtensionsList] = useState<ExtensionType[] | undefined>(undefined);
    const [ringGroupsList, setRingGroupsList] = useState<RingGroupType[] | undefined>(undefined);
    const [sipCallsList, setSipCallsList] = useState<SipCall[] | undefined>(undefined);

    const dispatch = useDispatch();
    const {t} = useTranslation();

    function refresh(data: ExtensionPresenceWidgetData) {
        setExtensionsList(data.extensionsList);
        setRingGroupsList(data.ringGroupsList);
        setSipCallsList(data.sipCallsList);

        dispatch(actions.refreshWidgetCompleted.request(id));
        initRender.current = false
    }

    const isLoadingChart = useMemo(() => {
        const itm = refreshWidgetsQueue?.find(e => e.id === id);

        if(!itm) return false;

        return !(itm && itm.dataHasLoaded);

    }, [refreshWidgetsQueue, id]);

    useEffect(() => {
        const itm = refreshWidgetsQueue?.find(e => e.id === id);
        if (itm && itm.dataHasLoaded) {
            refresh(itm.data as ExtensionPresenceWidgetData);
        }
    }, [refreshWidgetsQueue]);

    useEffect(() => {
        let intervalId:Timeout;

        if (autoRefreshTime) {
            intervalId = setInterval(() => {
                dispatch(actions.refreshWallboardTrigger.request({
                    refreshImmediately: true,
                    id: id
                }));
            }, autoRefreshTime * 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [autoRefreshTime]);

    const filteredExtensions = useMemo(() => {
        switch (data.show) {
            case WidgetExtensionPresenseFilter.selected_extensions:
                return filterBySelectedExtensions(data, extensionsList);
            case WidgetExtensionPresenseFilter.selected_ring_groups:
                return filterBySelectedRingGroups(data, extensionsList, ringGroupsList);
            default:
                return extensionsList ?? [];
        }
    }, [data, extensionsList, ringGroupsList]);

    const extensionStatuses = useMemo(() => {
        return filteredExtensions.map(e => getStatus(e, sipCallsList ?? []));
    }, [filteredExtensions, sipCallsList]);

    const onCallExtensions = useMemo(() => {
        const data = extensionStatuses
            .filter(e => e.isOnCall)
            .map(e => e.extension);

        return Array.from(new Set(data));
    }, [extensionStatuses]);

    const onCall = useMemo(() => {
        return onCallExtensions?.length || 0;
    }, [onCallExtensions]);

    const ringingExtensions = useMemo(() => {
        const exts = extensionStatuses
            .filter(e => e.isOnRinging)
            .map(e => e.extension);
        return Array.from(new Set(exts));
    }, [extensionStatuses]);

    const ringing = useMemo(() => {
        return ringingExtensions?.length || 0;
    }, [ringingExtensions]);

    const unregisteredExtensions = useMemo(() => {
        return getUnregisteredExtensions(filteredExtensions, onCallExtensions, ringingExtensions);
    }, [filteredExtensions, onCallExtensions, ringingExtensions]);

    const unregisteredCount = useMemo(() => {
        if(isChecked) {
            return 0
        }

        return unregisteredExtensions?.length || 0;
    }, [unregisteredExtensions, isChecked]);

    const onHoldExtensions = useMemo(() => {
        const exts = extensionStatuses
            .filter(e => e.isOnHold)
            .map(e => e.extension);
        return Array.from(new Set(exts));
    }, [extensionStatuses]);

    const onHold = useMemo(() => {
        return onHoldExtensions?.length || 0;
    }, [onHoldExtensions]);

    const availableExtensions = useMemo(() => {
        if (data.show === WidgetExtensionPresenseFilter.selected_ring_groups) {
            return getAvailableExtensionsFromRingGroups(data, extensionStatuses, ringGroupsList);
        }
        return [];
    }, [data, ringGroupsList, extensionStatuses]);

    const available = useMemo(() => {
        return availableExtensions?.length || 0;
    }, [availableExtensions]);

    const loggedOutExtensions = useMemo(() => {
        if (data.show === WidgetExtensionPresenseFilter.selected_ring_groups) {
            return getLoggedOutExtensionsFromRingGroups(data, extensionStatuses, ringGroupsList);
        }
        return [];
    }, [data, ringGroupsList, extensionStatuses]);

    const loggedOut = useMemo(() => {
        return loggedOutExtensions?.length || 0;
    }, [loggedOutExtensions]);

    const idleExtensions = useMemo(() => {
        const exts = extensionStatuses
            .filter(e => e.isIdle)
            .map(e => e.extension)
            ?.filter(e => !(!!availableExtensions.find(d => d === e)))
            ?.filter(e => !(!!loggedOutExtensions.find(d => d === e)));
        return Array.from(new Set(exts));
    }, [extensionStatuses, availableExtensions, loggedOutExtensions]);

    const idleCount = useMemo(() => {
        return idleExtensions?.length || 0;
    }, [idleExtensions]);

    const parkedExtensions = useMemo(() => {
        const exts = extensionStatuses
            .filter(e => e.isParked)
            .map(e => e.extension);
        return Array.from(new Set(exts));
    }, [extensionStatuses]);

    const chartData = useMemo(() => {
        let retArray = []

        if (!onCall && !onHold && !ringing && !loggedOut && !idleCount && !unregisteredCount && !available) {
            retArray.push({
                label: '',
                value: 1,
                color: darkMode ? Colors.DarkMode : Colors.NormalMode,
                testId: 'empty-chart-item',
            } as ChartLegendItem)
        } else {
            retArray = [
                {
                    label: t('screens:wallboard.onCallCount'),
                    value: onCall,
                    color: Colors.Error,
                    testId: 'on-call-label'
                } as ChartLegendItem,
                {
                    label: t('screens:wallboard.onHoldCount'),
                    value: onHold,
                    color: Colors.Error,
                    testId: 'on-hold-label'
                } as ChartLegendItem,
                {
                    label: t('screens:wallboard.ringingCount'),
                    value: ringing,
                    color: Colors.Support,
                    testId: 'ringing-label'
                } as ChartLegendItem
            ];

            if (data.show === WidgetExtensionPresenseFilter.selected_ring_groups) {
                retArray.push(
                    {
                        label: t('screens:wallboard.loggedOutCounts'),
                        value: loggedOut,
                        color: Colors.Primary,
                        testId: 'logged-out-label'
                    } as ChartLegendItem);
            } else {
                retArray.push(
                    {
                        label: t('screens:wallboard.idleCounts'),
                        value: idleCount,
                        color: Colors.Support,
                        testId: 'idle-label'
                    } as ChartLegendItem);
            }

            retArray.push(
                {
                    label: t('screens:wallboard.unregisteredCount'),
                    value: unregisteredCount,
                    color: Colors.Primary,
                    testId: 'unregistered-label'
                } as ChartLegendItem
            );

            if (data.show === WidgetExtensionPresenseFilter.selected_ring_groups) {
                retArray.push(
                    {
                        label: t('screens:wallboard.availableCount'),
                        value: available,
                        color: Colors.Support,
                    } as ChartLegendItem);
            }
        }

        return retArray;
    }, [onCall, onHold, ringing, idleCount, unregisteredCount, loggedOut, available, data, darkMode]);

    const dataset = useMemo(() => {
        return {
            labels: chartData.map((v) => v.label),
            datasets: [
                {
                    data: chartData.map((v) => v.value),
                    isEmpty: chartData.length === 1 && chartData[0].testId === 'empty-chart-item',
                    backgroundColor: chartData.map((v) => v.color),
                    borderColor: chartData.map(() => darkMode ? '#201F2B' : Colors.White),
                },
            ],
        };
    }, [chartData, darkMode]);

    const itemsToShow = useMemo(() => {
        if (!filteredExtensions.length) return [];

        if (!isChecked) return filteredExtensions;

        const unregisteredSet = new Set(unregisteredExtensions.map(e => e.extension_id));

        return filteredExtensions.filter(e => !unregisteredSet.has(e.extension_id));
    }, [filteredExtensions, unregisteredExtensions, isChecked]);


    const dataSum = dataset.datasets[0].isEmpty ? 0 : dataset.datasets[0].data.reduce((v, w) => v + w, 0);

    const isEmptyData = useMemo(() => {
        return !isLoadingChart && itemsToShow != undefined && itemsToShow.length === 0;
    }, [itemsToShow, isLoadingChart]);

    return (<>
        <div id={'extension_presense_widget_' + id}
            className={classNames(classes.widgetMainContainer,
                presentation === WidgetPresantationType.list && classes.overflowStyleForListWidgetType,
                presentation === WidgetPresantationType.doughnut && classes.centerFlex
            )}>

            {presentation === WidgetPresantationType.doughnut && (
                <div className={classes.mainContainer}>
                    <div className={classes.nativeSelectContainer} id={'native-select-container' + id}>
                    </div>

                    <div className={classes.chartMainContainer}>
                        <div className={classNames(classes.chartContainer,
                            (isEmptyData || darkMode) && classes.chartContainerEmptyData)}>
                            <div
                                className={classNames(
                                    classes.chartPlaceholder,
                                    isLoadingChart && classes.chartLoadingBackground,
                                    isEmptyData && classes.chartEmptyBackground,
                                )}
                            />
                            {!isLoadingChart && (
                                <Doughnut
                                    className={classes.chart}
                                    data={dataset}
                                    options={{
                                        responsive: true,
                                        //@ts-ignore
                                        cutout: '72%',
                                        //@ts-ignore
                                        borderWidth: isEmptyData ? 0 : 1,
                                        plugins: {
                                            legend: {
                                                display: false,
                                            },
                                            tooltip: isEmptyData ? {enabled: false} : {
                                                enabled: true,
                                                backgroundColor: 'rgba(51, 51, 51, 1)'
                                            }
                                        },
                                        maintainAspectRatio: false
                                    }}
                                />
                            )}

                            {!isLoadingChart && (
                                <div className={classes.sumValue}>
                                    <AnimatedNumberText
                                        className={classNames(classes.sumText, darkMode && classes.sumTextDarkMode)}
                                        hideThousands
                                        value={dataSum}
                                        dataTestId="doughnut-chart-sum-value"
                                        dataQa="doughnut-chart-sum-value"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {chartData.length > 1 ? (
                        <div className={classes.legendContainer}>
                            <div className={classes.legendRow}>
                                <div className={classes.legendSubRow}>
                                    <div>
                                        <WidgetLegendItem itm={chartData[0]} isLoadingChart={isLoadingChart}
                                            darkMode={darkMode}/>
                                    </div>
                                    <div>
                                        <WidgetLegendItem itm={chartData[1]} isLoadingChart={isLoadingChart}
                                            darkMode={darkMode}/>
                                    </div>
                                    {chartData.length === 6 &&
                                        <div>
                                            <WidgetLegendItem itm={chartData[4]} isLoadingChart={isLoadingChart}
                                                darkMode={darkMode}/>
                                        </div>
                                    }
                                </div>
                                <div className={classNames(classes.legendSubRow, classes.secondColumn)}>
                                    <div>
                                        <WidgetLegendItem itm={chartData[2]} isLoadingChart={isLoadingChart}
                                            darkMode={darkMode}/>
                                    </div>
                                    <div>
                                        <WidgetLegendItem itm={chartData[3]} isLoadingChart={isLoadingChart}
                                            darkMode={darkMode}/>
                                    </div>
                                    {chartData.length === 6 &&
                                        <div>
                                            <WidgetLegendItem itm={chartData[5]} isLoadingChart={isLoadingChart}
                                                darkMode={darkMode}/>
                                        </div>
                                    }
                                </div>
                            </div>
                            {chartData.length === 5 &&
                                <div className={classNames(classes.legendRow, classes.lastItem)}>
                                    <WidgetLegendItem itm={chartData[4]} isLoadingChart={isLoadingChart}
                                                      darkMode={darkMode}/>
                                </div>
                            }
                        </div>
                    ) : <div className={classes.legendContainer}></div>}


                </div>
            )}
            {presentation === WidgetPresantationType.list && (
                <div
                    className={classNames(classes.listViewMainContainer, isEmptyData && classes.listViewMainContainerNoData)}
                >

                    <WidgetHeaderWrapper
                        headerTitle={t('screens:wallboard.extension') }
                        id={'native-select-container' + id}
                        onChange={() => setIsChecked(prevState => !prevState)}
                        darkMode={darkMode}
                        checkboxValue={isChecked}
                        showTitle={(!isLoadingChart && !isEmptyData) || false}
                        checkboxLabel={t('screens:wallboard.hideUnregister')}
                    />

                    <div className={classes.listViewItemsContainer}>
                        {
                            itemsToShow.map(e =>
                                (
                                    <ExtensionStatusItem
                                        key={e.extension_id}
                                        e={e}
                                        id={id}
                                        darkMode={darkMode}
                                        groups={{
                                            unregistered: unregisteredExtensions,
                                            idle: idleExtensions,
                                            onCall: onCallExtensions,
                                            onHold: onHoldExtensions,
                                            ringing: ringingExtensions,
                                            available: availableExtensions,
                                            loggedOut: loggedOutExtensions,
                                            parked: parkedExtensions
                                        }}
                                        sipCallsList={sipCallsList}
                                    />
                                )
                            )
                        }
                    </div>

                        {
                            <EmptyGridPlaceholder
                                showNoDataImg
                                message={t('common:noData')}
                                customClass={classes.noData}
                                isVisible={!initRender.current && !isLoadingChart && isEmptyData}
                            />
                        }

                </div>
            )}
        </div>
        <WidgetLoadingMask isLoadingChart={isLoadingChart} darkMode={darkMode}/>
    </>);
};

export default ExtensionPresenceWidget;